import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Sparkles, Loader2, Copy } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONTENT_TYPES = [
  { id: 'infolettre', label: 'Infolettre' },
  { id: 'annonce', label: 'Annonce' },
  { id: 'reseaux sociaux', label: 'Réseaux sociaux' },
  { id: 'description événement', label: 'Événement' },
  { id: 'remerciement donateurs', label: 'Remerciement' },
];

export const AdminRedactionTab = ({ getAuthHeaders }) => {
  const [prompt, setPrompt] = useState('');
  const [contentType, setContentType] = useState('infolettre');
  const [result, setResult] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const generate = async () => {
    if (!prompt.trim()) {
      toast.error('Décrivez le texte à rédiger.');
      return;
    }
    setIsGenerating(true);
    setResult('');
    try {
      const res = await axios.post(`${API}/assistant/admin/generate`,
        { prompt: prompt.trim(), content_type: contentType },
        { headers: getAuthHeaders() });
      setResult(res.data.text);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de génération.');
    }
    setIsGenerating(false);
  };

  const copy = () => {
    navigator.clipboard.writeText(result);
    toast.success('Texte copié !');
  };

  return (
    <div data-testid="admin-redaction-tab">
      <h2 className="font-serif text-2xl font-bold text-[#1A252C] mb-6 flex items-center gap-2">
        <Sparkles className="w-6 h-6 text-[#D4AF37]" />
        Rédaction IA
      </h2>
      <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 space-y-5 max-w-3xl">
        <div>
          <p className="text-sm font-medium text-[#546A76] mb-2">Type de contenu</p>
          <div className="flex flex-wrap gap-2">
            {CONTENT_TYPES.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setContentType(t.id)}
                data-testid={`redaction-type-${t.id.replace(/ /g, '-')}`}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  contentType === t.id ? 'bg-[#4A708B] text-white' : 'bg-[#4A708B]/10 text-[#4A708B] hover:bg-[#4A708B]/20'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-sm font-medium text-[#546A76] mb-2">Que souhaitez-vous rédiger ?</p>
          <Textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Ex : Une infolettre annonçant le lancement de notre boutique solidaire avec la combinaison sport à 65$..."
            rows={4}
            data-testid="redaction-prompt"
            className="rounded-xl"
          />
        </div>
        <Button onClick={generate} disabled={isGenerating} data-testid="redaction-generate-btn" className="gold-btn rounded-full px-6">
          {isGenerating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {isGenerating ? 'Rédaction en cours...' : 'Rédiger le texte'}
        </Button>
        {result && (
          <div className="bg-[#FAFAFA] border border-[#E2E8F0] rounded-2xl p-5" data-testid="redaction-result">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-[#1A252C]">Texte généré</p>
              <button type="button" onClick={copy} data-testid="redaction-copy-btn" className="inline-flex items-center gap-1.5 text-sm text-[#4A708B] hover:text-[#3B5A6F] font-medium">
                <Copy className="w-4 h-4" /> Copier
              </button>
            </div>
            <p className="text-sm text-[#1A252C] whitespace-pre-wrap leading-relaxed">{result}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminRedactionTab;
